import { useEffect, useRef, useState } from "react";
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from "livekit-client";

interface QueryParams {
  url: string;
  token: string;
  layout: string;
  /**
   * Optional override carried via query string when we want the
   * watermark to show a friendly session ID instead of the LiveKit room
   * name. The egress recorder doesn't pass this — sidecar appends it
   * when constructing the customBaseUrl. Falls back to the room name
   * once we connect.
   */
  sessionId: string | null;
}

function readQueryParams(search: string): QueryParams | null {
  const sp = new URLSearchParams(search);
  const url = sp.get("url");
  const token = sp.get("token");
  if (!url || !token) return null;
  return {
    url,
    token,
    layout: sp.get("layout") ?? "default",
    sessionId: sp.get("sessionId"),
  };
}

/**
 * Top-level recorder view. The page is rendered inside a headless
 * Chrome instance owned by `livekit-egress`; once we log
 * `START_RECORDING` to the console, the egress encoder begins
 * capturing the page's video output + the browser's mixed audio
 * output. Logging `END_RECORDING` flushes and finalizes the file.
 *
 * Lifecycle:
 *   - Mount → parse query → connect Room as recorder.
 *   - First successful `Connected` event → log START_RECORDING.
 *   - `Disconnected` event → log END_RECORDING (egress closes file).
 *   - Component unmount → graceful disconnect (ditto).
 *
 * The egress encoder doesn't care what's *visually* on the page when
 * START_RECORDING fires — it just begins capturing. So if the
 * client's screen share isn't published yet (rare; the sidecar
 * triggers egress on `participant_joined` for the client, which
 * usually publishes screen share milliseconds later), the recording
 * shows a brief black-screen prologue with audio. That's fine.
 */
export function App() {
  const [params] = useState(() => readQueryParams(window.location.search));
  const [error, setError] = useState<string | null>(null);
  const [screenAttached, setScreenAttached] = useState(false);
  const [watermark, setWatermark] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!params) {
      setError("Missing required ?url and ?token parameters.");
      return;
    }

    const room = new Room({
      adaptiveStream: false,
      dynacast: false,
    });
    let mounted = true;
    let didLogStart = false;

    /** Active <video> + <audio> elements we own, keyed by track SID. */
    const attachedElements = new Map<string, HTMLMediaElement[]>();

    function logStartOnce(): void {
      if (didLogStart) return;
      didLogStart = true;
      // Egress recorder waits for this exact line. See LiveKit egress
      // custom template docs.
      console.log("START_RECORDING");
    }

    function detach(trackSid: string): void {
      const elements = attachedElements.get(trackSid);
      if (!elements) return;
      for (const el of elements) el.remove();
      attachedElements.delete(trackSid);
    }

    function handleSubscribed(
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      _participant: RemoteParticipant,
    ): void {
      const sid = publication.trackSid;

      if (track.kind === Track.Kind.Audio) {
        // `track.attach()` returns a fresh <audio> element, mutes
        // muted, autoplays, and the browser mixes it with any other
        // attached audio elements. Egress captures the page's audio
        // output stream, so this is the multi-mic mix path.
        const el = track.attach();
        document.body.appendChild(el);
        attachedElements.set(sid, [el]);
        return;
      }

      if (track.kind === Track.Kind.Video && publication.source === Track.Source.ScreenShare) {
        // We only render the screen-share track. Camera tracks (rare
        // in our flow — neither side enables webcam) are deliberately
        // ignored to keep the canvas clean.
        const el = track.attach() as HTMLVideoElement;
        if (stageRef.current) {
          // Replace any prior video (e.g. after Tier 1 re-publish).
          stageRef.current.querySelectorAll("video").forEach((v) => {
            v.remove();
          });
          stageRef.current.appendChild(el);
          attachedElements.set(sid, [el]);
          if (mounted) setScreenAttached(true);
        }
      }
    }

    function handleUnsubscribed(_track: RemoteTrack, publication: RemoteTrackPublication): void {
      const sid = publication.trackSid;
      detach(sid);
      if (publication.source === Track.Source.ScreenShare && mounted) {
        setScreenAttached(false);
      }
    }

    function handleConnected(): void {
      if (!mounted) return;
      const fallback = params?.sessionId ?? room.name;
      setWatermark(fallback);
      logStartOnce();
    }

    function handleDisconnected(): void {
      // Egress recorder waits for this exact line to flush the muxer
      // and close the output file.
      console.log("END_RECORDING");
    }

    room.on(RoomEvent.TrackSubscribed, handleSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, handleUnsubscribed);
    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);

    room.connect(params.url, params.token, { autoSubscribe: true }).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (mounted) setError(`Failed to join recording room: ${msg}`);
      // Even on connect failure we should signal end so the egress
      // recorder finalizes the (empty) output instead of hanging.
      console.log("END_RECORDING");
    });

    return () => {
      mounted = false;
      room.off(RoomEvent.TrackSubscribed, handleSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, handleUnsubscribed);
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      // Best-effort cleanup. The egress recorder typically tears down
      // the Chrome instance after END_RECORDING anyway.
      void room.disconnect();
      for (const elements of attachedElements.values()) {
        for (const el of elements) el.remove();
      }
      attachedElements.clear();
    };
  }, [params]);

  return (
    <div className="stage" ref={stageRef}>
      {!screenAttached && !error && <div className="placeholder">Waiting for screen share…</div>}
      {error && <div className="error">{error}</div>}
      {watermark && <div className="watermark">Session {formatSessionId(watermark)}</div>}
    </div>
  );
}

/**
 * Trim a UUID-style session ID to its first segment for the watermark
 * — full UUIDs are too long for the corner overlay.
 */
function formatSessionId(raw: string): string {
  if (raw.length <= 12) return raw;
  return `${raw.slice(0, 8)}…`;
}
